import { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar, SnackbarCloseReason } from '@mui/material';
import * as yup from 'yup';
import { VTextField, VForm, useVForm, IVFormErrors, BasicSelect } from '../../../shared/forms';
import { BensService, TTipoBem } from '../../../shared/services/api/servicos/BensService';

interface IFormData {
  tipo: TTipoBem;
  descricao: string;
  marca?: string;
  modelo?: string;
  placa?: string;
  patrimonio?: string;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  tipo: yup.mixed<TTipoBem>().oneOf(['VEICULO', 'MOTO', 'MAQUINA', 'EQUIPAMENTO', 'PATRIMONIO', 'OUTRO']).required('Tipo é obrigatório'),
  descricao: yup.string().required('Descrição é obrigatória').min(2).max(255),
  marca: yup.string().optional().default(''),
  modelo: yup.string().optional().default(''),
  placa: yup.string().optional().default(''),
  patrimonio: yup.string().optional().default(''),
});

const TIPOS_BEM = [
  { nameBD: 'VEICULO', descricao: 'Veículo' },
  { nameBD: 'MOTO', descricao: 'Moto' },
  { nameBD: 'MAQUINA', descricao: 'Máquina' },
  { nameBD: 'EQUIPAMENTO', descricao: 'Equipamento' },
  { nameBD: 'PATRIMONIO', descricao: 'Patrimônio' },
  { nameBD: 'OUTRO', descricao: 'Outro' },
];

interface IDialogCadastrarBemProps {
  open: boolean;
  idparceiro: string;
  onClose: () => void;
  aoCriar: (idbem: string) => void;
}

// Cadastro rápido de bem sem sair do fluxo de abertura da OS — mesmo
// espírito de um dialog de cadastro rápido dentro de outra tela (ver
// DialogCadastrarProd na tela de Entrada por XML). Só um subconjunto dos
// campos de DetalheDeBem.tsx; o restante pode ser completado depois direto
// no cadastro de bens.
export const DialogCadastrarBem: React.FC<IDialogCadastrarBemProps> = ({ open, idparceiro, onClose, aoCriar }) => {
  const { formRef, save } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [tipoSelecionado, setTipoSelecionado] = useState<TTipoBem | ''>('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'>('warning');

  const ehVeiculo = tipoSelecionado === 'VEICULO' || tipoSelecionado === 'MOTO';
  const ehOutro = tipoSelecionado === 'MAQUINA' || tipoSelecionado === 'EQUIPAMENTO' || tipoSelecionado === 'PATRIMONIO';

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const handleSave = (dados: IFormData) => {
    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        BensService
          .create({ ...dadosValidados, idparceiro, ativo: true })
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTipoSelecionado('');
              formRef.current?.setData({ tipo: '', descricao: '', marca: '', modelo: '', placa: '', patrimonio: '' });
              aoCriar(result);
              onClose();
            }
          });
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Cadastrar novo bem</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={null} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>
        <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Box>
            <Grid container spacing={2} padding={1}>
              <Grid item xs={12} sm={6}>
                <BasicSelect
                  name='tipo'
                  label='Tipo'
                  data={TIPOS_BEM}
                  aoClicar={(valor) => setTipoSelecionado((valor as TTipoBem) ?? '')}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <VTextField size='small' fullWidth name='descricao' disabled={isLoading} label='Descrição' />
              </Grid>
              {(ehVeiculo || ehOutro) && (
                <>
                  <Grid item xs={6}>
                    <VTextField size='small' fullWidth name='marca' disabled={isLoading} label='Marca' />
                  </Grid>
                  <Grid item xs={6}>
                    <VTextField size='small' fullWidth name='modelo' disabled={isLoading} label='Modelo' />
                  </Grid>
                </>
              )}
              {ehVeiculo && (
                <Grid item xs={6}>
                  <VTextField size='small' fullWidth name='placa' disabled={isLoading} label='Placa' />
                </Grid>
              )}
              {ehOutro && (
                <Grid item xs={6}>
                  <VTextField size='small' fullWidth name='patrimonio' disabled={isLoading} label='Patrimônio' />
                </Grid>
              )}
            </Grid>
          </Box>
        </VForm>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
        <Button variant='contained' onClick={save} disabled={isLoading}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};
